import React, { useState } from 'react';
import { Pencil, Target } from 'lucide-react';
import { useIntl } from 'react-intl';
import NotesModal from './NotesModal';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import { isBlankNoteHtml, sanitizeHtml, htmlMapHasContent } from '../../utils/sanitizeHtml';
import Spinner from '../common/Spinner';
import { useToast } from '../common/Toast';
import './MonthlyFocusCard.scss';

function monthKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

interface MonthlyFocusCardProps {
  date: Date;
}

const MonthlyFocusCard: React.FC<MonthlyFocusCardProps> = ({ date }) => {
  const { formatMessage: t } = useIntl();
  const { settings, isLoading, updateSettings } = useUserPreferences();
  const { showToast } = useToast();
  const key = monthKey(date);
  const saved = settings.monthlyFocus?.[key] ?? '';
  const [editing, setEditing] = useState(false);
  const hasContent = !isBlankNoteHtml(saved);
  const focusFirstTime = !htmlMapHasContent(settings.monthlyFocus);

  const save = async (html: string) => {
    try {
      await updateSettings({
        monthlyFocus: {
          ...(settings.monthlyFocus ?? {}),
          [key]: html,
        },
      });
      showToast(t({ id: 'toast.focus.saved' }));
    } catch {
      showToast(t({ id: 'toast.focus.saveFailed' }), 'error');
      throw new Error('save failed');
    }
  };

  return (
    <>
      <div className="monthly-focus-card" onClick={() => setEditing(true)}>
        <div className="monthly-focus-card__fold" />
        <div className="monthly-focus-card__head">
          <Target className="monthly-focus-card__icon" size={15} strokeWidth={2} aria-hidden />
          <span className="monthly-focus-card__title">
            {t({ id: 'daily.focus.title' })}
          </span>
          <span className="monthly-focus-card__sep" aria-hidden>
            ·
          </span>
          <button
            type="button"
            className="monthly-focus-card__edit"
            onClick={() => setEditing(true)}
            aria-label={t({ id: 'daily.focus.edit' })}
            title={t({ id: 'daily.focus.edit' })}
          >
            <Pencil size={14} />
          </button>
        </div>
        {isLoading ? (
          <Spinner block size="sm" />
        ) : hasContent ? (
          <button
            type="button"
            className="monthly-focus-card__text note-html"
            onClick={() => setEditing(true)}
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(saved) }}
          />
        ) : (
          <button type="button" className="monthly-focus-card__text monthly-focus-card__text--empty" onClick={() => setEditing(true)}>
            <span className="empty-copy__body">
              {t({ id: focusFirstTime ? 'daily.focus.placeholder' : 'daily.focus.placeholderShort' })}
            </span>
            {focusFirstTime ? (
              <span className="empty-copy__example">{t({ id: 'daily.focus.placeholderExample' })}</span>
            ) : null}
          </button>
        )}
      </div>

      <NotesModal
        isOpen={editing}
        onClose={() => setEditing(false)}
        notes={saved}
        onSave={save}
        title={t({ id: 'daily.focus.title' })}
        placeholder={t({ id: focusFirstTime ? 'daily.focus.placeholder' : 'daily.focus.placeholderShort' })}
      />
    </>
  );
};

export default MonthlyFocusCard;
